import React, { useMemo } from 'react';
import { Tree, Tag, Empty, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { UserOutlined, TeamOutlined, CrownOutlined } from '@ant-design/icons';

const { Text } = Typography;

// KAN-38 Phase 5: read-only tree visualisation of users assigned to a yacht.
// Hierarchy:
//   root            asset_manager(s) — Captain
//     department    grouped by user_asset_assignments.department
//       crew        all other users on the asset in that department
// Department-less users land in a generic "Unassigned department" bucket so
// nothing is hidden. Per-node SIM counts are deferred to a follow-up so this
// component can ship without a new backend endpoint.

export interface YachtCrewMember {
  id: number;
  user_id: number;
  department: string | null;
  role_on_asset: string;
  email: string;
  first_name: string;
  last_name: string;
  user_role: string; // platform role: super_admin, asset_manager, department_head, crew, ...
}

interface YachtUserTreeProps {
  crew: YachtCrewMember[];
}

function roleColor(role: string): string {
  switch (role) {
    case 'super_admin':
      return 'red';
    case 'reseller_admin':
      return 'orange';
    case 'customer_admin':
      return 'blue';
    case 'asset_manager':
      return 'green';
    case 'department_head':
      return 'cyan';
    case 'crew':
      return 'default';
    default:
      return 'default';
  }
}

function displayName(m: YachtCrewMember): string {
  const n = `${m.first_name || ''} ${m.last_name || ''}`.trim();
  return n || m.email;
}

function userLabel(m: YachtCrewMember): React.ReactNode {
  return (
    <span>
      {displayName(m)}{' '}
      <Tag color={roleColor(m.user_role)} style={{ marginRight: 4 }}>
        {m.user_role}
      </Tag>
      {m.role_on_asset && m.role_on_asset !== m.user_role ? (
        <Tag color="purple">{m.role_on_asset}</Tag>
      ) : null}
      {m.department ? (
        <Text type="secondary" style={{ marginLeft: 4 }}>
          · {m.department}
        </Text>
      ) : null}
    </span>
  );
}

const UNASSIGNED = '__unassigned__';

const YachtUserTree: React.FC<YachtUserTreeProps> = ({ crew }) => {
  const treeData = useMemo<DataNode[]>(() => {
    if (!crew || crew.length === 0) return [];

    const captains = crew.filter(c => c.user_role === 'asset_manager');
    const deptHeads = crew.filter(c => c.user_role === 'department_head');
    const rest = crew.filter(
      c => c.user_role !== 'asset_manager' && c.user_role !== 'department_head'
    );

    // Group dept heads and crew by department.
    const byDept = new Map<string, { heads: YachtCrewMember[]; members: YachtCrewMember[] }>();
    function bucket(dept: string | null) {
      const key = dept && dept.trim() ? dept : UNASSIGNED;
      if (!byDept.has(key)) byDept.set(key, { heads: [], members: [] });
      return byDept.get(key)!;
    }
    deptHeads.forEach(h => bucket(h.department).heads.push(h));
    rest.forEach(c => bucket(c.department).members.push(c));

    function deptNode(deptKey: string): DataNode {
      const { heads, members } = byDept.get(deptKey)!;
      const label = deptKey === UNASSIGNED ? 'Unassigned department' : deptKey;
      const headsNode: DataNode[] = heads.map(h => ({
        key: `head-${h.id}`,
        title: userLabel(h),
        icon: <UserOutlined />,
        children: members
          .filter(m => true /* dept already filtered into this bucket */)
          .map(m => ({
            key: `member-${m.id}`,
            title: userLabel(m),
            icon: <UserOutlined />,
            isLeaf: true,
          })),
      }));
      // Members without a department head: leaves directly under the dept node.
      const looseMembers: DataNode[] =
        heads.length === 0
          ? members.map(m => ({
              key: `member-${m.id}`,
              title: userLabel(m),
              icon: <UserOutlined />,
              isLeaf: true,
            }))
          : [];
      return {
        key: `dept-${deptKey}`,
        title: (
          <span>
            <TeamOutlined style={{ marginRight: 6 }} />
            <Text strong>{label}</Text>
            <Text type="secondary"> ({heads.length + members.length})</Text>
          </span>
        ),
        children: [...headsNode, ...looseMembers],
      };
    }

    const deptNodes = Array.from(byDept.keys())
      .sort((a, b) => (a === UNASSIGNED ? 1 : b === UNASSIGNED ? -1 : a.localeCompare(b)))
      .map(deptNode);

    if (captains.length === 0) {
      // No captain — render the department nodes at the root.
      return deptNodes;
    }

    return captains.map(cap => ({
      key: `captain-${cap.id}`,
      title: (
        <span>
          <CrownOutlined style={{ color: '#faad14', marginRight: 6 }} />
          {userLabel(cap)}
        </span>
      ),
      children: deptNodes,
    }));
  }, [crew]);

  if (!crew || crew.length === 0) {
    return <Empty description="No crew assigned yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return <Tree showIcon defaultExpandAll treeData={treeData} selectable={false} />;
};

export default YachtUserTree;
